import React, { useMemo } from 'react';
import { Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { FieldWrapper, IField } from 'modules/views/pages/PersonalInformation/components/fieldWrapper';
import AddressAutocomplete from 'modules/views/shared/AddressAutocomplete/AddressAutocomplete';
import PiiFieldWrapper from 'modules/views/shared/PiiFieldWrapper';
import Select from 'modules/views/shared/Select';
import TextField from 'modules/views/shared/TextField';
import { IAddress } from 'services/GoogleMapsService';
import { useAppSelector } from 'modules/state-mgmt/store';
import { shouldShowCountyForCountry, shouldShowStateForCountry } from 'utils/addressValidationUtils';

import { IProfileInformationSchema } from '../schema';
import { useStyles } from '../styles';

interface IHomeAddressSectionProps {
  addressFields: IField[];
  allowedCountryCodes: string[];
  values: IProfileInformationSchema;
  errors: Record<string, { message: string } | undefined>;
  setValue: (key: keyof IProfileInformationSchema, value: any) => void;
  onAddressSelect: (address: IAddress) => void;
  onPiiFieldClick: () => void;
}

const HomeAddressSection: React.FC<IHomeAddressSectionProps> = ({
  addressFields,
  allowedCountryCodes,
  values,
  errors,
  setValue,
  onAddressSelect,
  onPiiFieldClick,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();

  const { countries, states, language } = useAppSelector(state => ({
    countries: state?.cache?.countries || {},
    states: state?.cache?.states || {},
    language: state?.app?.languageCode || 'en',
  }));

  const currentLanguageCountries = useMemo(() => countries[language] || [], [countries, language]);
  const currentLanguageStates = useMemo(() => states[language] || [], [states, language]);

  const selectedCountry = useMemo(
    () => currentLanguageCountries.find((c: any) => c.id === values.country),
    [currentLanguageCountries, values.country],
  );

  const stateOptions = useMemo(() => {
    if (!selectedCountry) return [];
    return currentLanguageStates
      .filter((state: any) =>
        state.country === selectedCountry.name || state.country === selectedCountry.id || state.country === selectedCountry.code,
      )
      .map((state: any) => ({
        value: state.code,
        label: state.name,
      }));
  }, [selectedCountry, currentLanguageStates]);

  const selectedStateOption = useMemo(() => {
    return stateOptions.find((option: any) => option.value === values.state) || null;
  }, [stateOptions, values.state]);

  const shouldShowState = useMemo(() => {
    return shouldShowStateForCountry(selectedCountry?.code);
  }, [selectedCountry]);

  const shouldShowCounty = useMemo(() => {
    return shouldShowCountyForCountry(selectedCountry?.code);
  }, [selectedCountry]);

  const shouldShowBorough = useMemo(() => {
    return values.state === 'NY' && !!values.borough;
  }, [values.state, values.borough]);

  if (addressFields.every(f => !f.isVisible)) return null;

  return (
    <>
      <p className={classes.sectionTitle} data-testid="profile-section-title-home-address">
        {t('contactInformation.homeAddress.title')}
      </p>
      <Grid container spacing={3} data-testid="profile-section-home-address">
        {addressFields.map((field, index) => {
          if (!field.isVisible) return null;

          if (field.id === 'addressLine1') {
            return (
              <Grid item xs={12} md={6} lg={4} key={field.id}>
                <PiiFieldWrapper
                  label={field.name}
                  required={field.isRequired}
                  piiFieldName="line1"
                  onClick={onPiiFieldClick}
                >
                  <AddressAutocomplete
                    label={field.name}
                    required={field.isRequired}
                    testId="addressLine1-input"
                    value={values.addressLine1}
                    error={errors.addressLine1?.message}
                    allowedCountries={allowedCountryCodes}
                    onChange={address => setValue('addressLine1', address)}
                    onAddressSelect={onAddressSelect}
                  />
                </PiiFieldWrapper>
              </Grid>
            );
          }

          if (field.id === 'addressLine2') {
            return (
              <Grid item xs={12} md={6} lg={4} key={field.id}>
                <PiiFieldWrapper
                  label={field.name}
                  required={field.isRequired}
                  piiFieldName="line2"
                  onClick={onPiiFieldClick}
                >
                  <TextField
                    label={field.name}
                    required={field.isRequired}
                    name="addressLine2"
                    testId="addressLine2-input"
                    value={values.addressLine2}
                    error={errors.addressLine2?.message}
                    onChange={e => setValue('addressLine2', e.target.value)}
                  />
                </PiiFieldWrapper>
              </Grid>
            );
          }

          if (field.id === 'state' && shouldShowState) {
            return (
              <Grid item xs={12} md={6} lg={4} key={field.id}>
                <PiiFieldWrapper
                  label={field.name}
                  required={field.isRequired}
                  piiFieldName="state"
                  onClick={onPiiFieldClick}
                >
                  <Select
                    inputLabel={field.name}
                    required={field.isRequired}
                    testId="state-select"
                    options={stateOptions}
                    selectedValue={selectedStateOption}
                    error={errors.state?.message || ''}
                    onChange={newValue => setValue('state', newValue?.value || '')}
                    disabled={!values.country}
                  />
                </PiiFieldWrapper>
              </Grid>
            );
          }

          if (field.id === 'state' && !shouldShowState) {
            return null;
          }

          return (
            <Grid item xs={12} md={6} lg={4} key={field.id}>
              <FieldWrapper
                field={field}
                index={index}
                value={values[field.id]}
                setValue={value => setValue(field.id as keyof IProfileInformationSchema, value)}
                error={errors[field.id]?.message}
                options={field.options}
                onPiiFieldClick={onPiiFieldClick}
              />
            </Grid>
          );
        })}
        {shouldShowCounty && (
          <Grid item xs={12} md={6} lg={4}>
            <PiiFieldWrapper
              label={t('contactInformation.homeAddress.county')}
              piiFieldName="county"
              onClick={onPiiFieldClick}
            >
              <TextField
                label={t('contactInformation.homeAddress.county')}
                name="county"
                testId="county-input"
                value={values.county}
                error={errors.county?.message}
                onChange={e => setValue('county', e.target.value)}
              />
            </PiiFieldWrapper>
          </Grid>
        )}
        {shouldShowBorough && (
          <Grid item xs={12} md={6} lg={4}>
            <PiiFieldWrapper
              label={t('contactInformation.homeAddress.borough')}
              piiFieldName="borough"
              onClick={onPiiFieldClick}
            >
              <TextField
                label={t('contactInformation.homeAddress.borough')}
                name="borough"
                testId="borough-input"
                value={values.borough}
                readOnly={true}
                disabled={true}
              />
            </PiiFieldWrapper>
          </Grid>
        )}
      </Grid>
    </>
  );
};

export default HomeAddressSection;
